// JIM-1006-05: when a Lite visit happened on the ground. Four stamps per
// visit — arrived (the phone reaching the site, or the form opening when the
// phone never told us), first answer, filed, left — kept in the draft and
// filed with the inspection; the leave time follows after filing.

export type ArrivalSource = 'location' | 'form';

export interface VisitTiming {
  opened_at?: string;
  arrived_at?: string;
  arrival_source?: ArrivalSource;
  arrival_distance_m?: number;
  first_answer_at?: string;
  filed_at?: string;
  left_at?: string;
}

export interface LatLng {
  latitude: number;
  longitude: number;
}

/** Within this of the pin counts as "at the site". Pins are good to ~10 m
 * when they come from the NOI or KMZ, and a subdivision is a few hundred
 * metres across. */
export const ARRIVAL_RADIUS_M = 250;
/** Leaving needs a wider circle than arriving so GPS wobble at the edge of
 * the site never ends a visit. */
export const LEAVE_RADIUS_M = 600;

export function distanceMeters(a: LatLng, b: LatLng): number {
  const r = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) *
      Math.cos(toRad(b.latitude)) *
      Math.sin(dLon / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

/** A site pin that is missing or the 0,0 placeholder cannot place a visit. */
export function hasPin(pin: Partial<LatLng> | null | undefined): pin is LatLng {
  return (
    !!pin &&
    typeof pin.latitude === 'number' &&
    typeof pin.longitude === 'number' &&
    !(pin.latitude === 0 && pin.longitude === 0)
  );
}

/** Fold one position fix into the timing: the first fix inside the arrival
 * circle sets arrived_at (if nothing better already did); a fix outside the
 * leave circle after arriving sets left_at once. Returns the same object
 * when nothing changed so callers can skip a re-render. */
export function foldPosition(
  timing: VisitTiming,
  pin: LatLng,
  here: LatLng,
  at: string
): VisitTiming {
  const d = distanceMeters(pin, here);
  if (!timing.arrived_at || timing.arrival_source === 'form') {
    if (d <= ARRIVAL_RADIUS_M) {
      return {
        ...timing,
        arrived_at: at,
        arrival_source: 'location',
        arrival_distance_m: Math.round(d),
      };
    }
    return timing;
  }
  if (
    timing.arrival_source === 'location' &&
    !timing.left_at &&
    d > LEAVE_RADIUS_M
  ) {
    return { ...timing, left_at: at };
  }
  return timing;
}

/** First answer typed: stamp once. */
export function markFirstAnswer(timing: VisitTiming, at: string): VisitTiming {
  return timing.first_answer_at ? timing : { ...timing, first_answer_at: at };
}

/** Filing the inspection: stamp filed_at, and when the phone never placed the
 * inspector at the site, fall back to when the form was opened. */
export function markFiled(timing: VisitTiming, at: string): VisitTiming {
  const next: VisitTiming = { ...timing, filed_at: timing.filed_at ?? at };
  if (!next.arrived_at && next.opened_at) {
    next.arrived_at = next.opened_at;
    next.arrival_source = 'form';
  }
  return next;
}

/** Leaving the flow after filing counts as leaving the site when the phone
 * has not said so itself. */
export function markLeft(timing: VisitTiming, at: string): VisitTiming {
  return timing.left_at ? timing : { ...timing, left_at: at };
}

/** What the API accepts: the stamps only, never the local opened_at. */
export function timingPayload(
  timing: VisitTiming
): Omit<VisitTiming, 'opened_at'> | undefined {
  const { opened_at: _opened, ...rest } = timing;
  const entries = Object.entries(rest).filter(
    ([, v]) => v !== undefined && v !== null
  );
  return entries.length
    ? (Object.fromEntries(entries) as VisitTiming)
    : undefined;
}

export function minutesBetween(
  start?: string | null,
  end?: string | null
): number | null {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  return Math.round(ms / 6000) / 10;
}
